import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { BarChart3, Share2, Newspaper, Store, Tag, Loader2, Users, Eye, Globe2, FileText, Download } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const CHANNEL_LABELS = {
  facebook: "Facebook", whatsapp: "WhatsApp", x: "X", linkedin: "LinkedIn",
  copy: "Lien copié", native: "Partage natif", other: "Autre",
};
const CHANNEL_COLORS = {
  facebook: "#1877F2", whatsapp: "#25D366", x: "#111827", linkedin: "#0A66C2",
  copy: "#64748b", native: "#E05638", other: "#94a3b8",
};
const PROV_COLORS = ["#E05638", "#1877F2", "#25D366", "#0A66C2", "#f0a23a", "#8b5cf6", "#111827", "#94a3b8"];

function ChannelBadge({ ch, n }) {
  if (!n) return null;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold text-white" style={{ background: CHANNEL_COLORS[ch] || "#94a3b8" }}>
      {CHANNEL_LABELS[ch] || ch} {n}
    </span>
  );
}

function TotalsBar({ channels, totals, grand }) {
  return (
    <div className="space-y-2" data-testid="share-channel-totals">
      {channels.map((c) => {
        const n = totals[c] || 0;
        const pct = grand > 0 ? Math.round((n / grand) * 100) : 0;
        return (
          <div key={c} className="flex items-center gap-3">
            <div className="w-24 text-xs font-semibold text-slate-600 shrink-0">{CHANNEL_LABELS[c] || c}</div>
            <div className="flex-1 h-5 rounded-full bg-slate-100 overflow-hidden">
              <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: CHANNEL_COLORS[c] || "#94a3b8", minWidth: n ? 6 : 0 }} />
            </div>
            <div className="w-14 text-right text-xs font-bold text-slate-900">{n}</div>
          </div>
        );
      })}
    </div>
  );
}

function TopTable({ title, icon: Icon, rows, channels, nameKey, linkFor }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid={`share-top-${nameKey}`}>
      <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2 font-heading font-extrabold text-slate-900">
        <Icon size={18} className="text-[#E05638]" /> {title}
      </div>
      {rows.length === 0 ? (
        <div className="p-8 text-center text-slate-400 text-sm">Aucun partage pour le moment.</div>
      ) : (
        <ul className="divide-y divide-slate-100">
          {rows.map((r, i) => (
            <li key={r.id || r.slug || i} className="p-4 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-semibold text-slate-900 truncate">
                  {linkFor(r) ? <a href={linkFor(r)} target="_blank" rel="noreferrer" className="hover:text-[#E05638]">{r[nameKey]}</a> : r[nameKey]}
                </div>
                <div className="mt-1.5 flex flex-wrap gap-1">
                  {channels.map((c) => <ChannelBadge key={c} ch={c} n={(r.shares_by || {})[c]} />)}
                </div>
              </div>
              <div className="shrink-0 text-right">
                <div className="text-xl font-extrabold text-[#E05638]">{r.shares || 0}</div>
                <div className="text-[10px] uppercase tracking-widest text-slate-400">partages</div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Kpi({ icon: Icon, label, value }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-5">
      <div className="text-xs uppercase tracking-widest text-slate-400 font-bold flex items-center gap-1.5"><Icon size={13} /> {label}</div>
      <div className="mt-1 text-3xl font-extrabold text-slate-900">{value}</div>
    </div>
  );
}

export default function AdminShareStats() {
  const [data, setData] = useState(null);
  const [an, setAn] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    api.get("/admin/share-stats").then((r) => setData(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/analytics?days=${days}`).then((r) => setAn(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [days]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/admin/analytics/export?days=${days}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `statistiques-${days}j.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Export CSV téléchargé.");
    } catch {
      toast.error("Échec de l'export CSV.");
    } finally {
      setExporting(false);
    }
  };

  const PERIODS = [
    { v: 7, label: "7 jours" },
    { v: 30, label: "30 jours" },
    { v: 90, label: "90 jours" },
  ];

  const channels = data?.channels || [];
  const site = process.env.REACT_APP_BACKEND_URL;
  const provTotal = (an?.provenance || []).reduce((s, p) => s + p.count, 0) || 1;

  return (
    <div data-testid="admin-stats-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-extrabold text-slate-900 flex items-center gap-2">
            <BarChart3 size={22} className="text-[#E05638]" /> Statistiques complètes
          </h1>
          <p className="text-sm text-slate-500 mt-1">Visiteurs, provenance du trafic et partages sur les réseaux sociaux.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-xl bg-slate-100 p-1" data-testid="stats-period-selector">
            {PERIODS.map((p) => (
              <button
                key={p.v}
                onClick={() => setDays(p.v)}
                data-testid={`stats-period-${p.v}`}
                className={`px-3 py-1.5 rounded-lg text-sm font-semibold transition-colors ${days === p.v ? "bg-white text-[#E05638] shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <button
            onClick={exportCsv}
            disabled={exporting}
            data-testid="stats-export-csv"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#E05638] text-white text-sm font-bold hover:bg-[#c7482c] transition-colors disabled:opacity-60"
          >
            {exporting ? <Loader2 className="animate-spin" size={16} /> : <Download size={16} />} Export CSV
          </button>
        </div>
      </div>

      {loading && !an ? (
        <div className="p-10 text-slate-500 flex items-center gap-2"><Loader2 className="animate-spin" size={18} /> Chargement…</div>
      ) : (
      <>
      {/* KPIs */}
      <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi icon={Users} label={`Visiteurs uniques (${days} j)`} value={an?.period_unique_visitors ?? 0} />
        <Kpi icon={Eye} label={`Visites (${days} j)`} value={an?.visits_period ?? 0} />
        <Kpi icon={Users} label="Visiteurs uniques (total)" value={an?.unique_visitors ?? 0} />
        <Kpi icon={Eye} label="Visites (total)" value={an?.total_visits ?? 0} />
      </div>

      {/* Courbe du jour, heure par heure */}
      <div className="mt-6 bg-white border border-slate-200 rounded-2xl p-5" data-testid="hourly-chart">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
          <div className="font-heading font-extrabold text-slate-900">Aujourd'hui, heure par heure <span className="text-slate-400 font-normal text-sm">({an?.today_label || ""})</span></div>
          <div className="flex items-center gap-4 text-sm" data-testid="hourly-today-totals">
            <span className="font-extrabold text-[#E05638]">{an?.visits_today ?? 0} <span className="font-normal text-slate-400">visites</span></span>
            <span className="font-extrabold text-[#1877F2]">{an?.visitors_today ?? 0} <span className="font-normal text-slate-400">visiteurs</span></span>
          </div>
        </div>
        <div style={{ width: "100%", height: 220 }}>
          <ResponsiveContainer>
            <AreaChart data={an?.hourly_today || []} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="gvh" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#E05638" stopOpacity={0.35} />
                  <stop offset="95%" stopColor="#E05638" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="guh" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#1877F2" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#1877F2" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
              <XAxis dataKey="hour" tick={{ fontSize: 10, fill: "#94a3b8" }} interval={1} />
              <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} allowDecimals={false} />
              <Tooltip />
              <Area type="monotone" dataKey="visits" name="Visites" stroke="#E05638" strokeWidth={2} fill="url(#gvh)" />
              <Area type="monotone" dataKey="visitors" name="Visiteurs" stroke="#1877F2" strokeWidth={2} fill="url(#guh)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Courbe des visites */}
      <div className="mt-6 bg-white border border-slate-200 rounded-2xl p-5" data-testid="visits-chart">
        <div className="font-heading font-extrabold text-slate-900 mb-4">Visites sur les {days} derniers jours</div>
        <div style={{ width: "100%", height: 260 }}>
          <ResponsiveContainer>
            <AreaChart data={an?.series || []} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="gv" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#E05638" stopOpacity={0.35} />
                  <stop offset="95%" stopColor="#E05638" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gu" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#1877F2" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#1877F2" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
              <XAxis dataKey="day" tick={{ fontSize: 10, fill: "#94a3b8" }} interval={days <= 7 ? 0 : days >= 90 ? 9 : 4} />
              <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} allowDecimals={false} />
              <Tooltip />
              <Area type="monotone" dataKey="visits" name="Visites" stroke="#E05638" strokeWidth={2} fill="url(#gv)" />
              <Area type="monotone" dataKey="visitors" name="Visiteurs" stroke="#1877F2" strokeWidth={2} fill="url(#gu)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Provenance + Top pages */}
      <div className="mt-6 grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="provenance-block">
          <div className="font-heading font-extrabold text-slate-900 mb-4 flex items-center gap-2"><Globe2 size={18} className="text-[#E05638]" /> Provenance du trafic</div>
          {(an?.provenance || []).length === 0 ? (
            <div className="p-6 text-center text-slate-400 text-sm">Pas encore de données de visite.</div>
          ) : (
            <div className="space-y-2">
              {(an?.provenance || []).map((p, i) => {
                const pct = Math.round((p.count / provTotal) * 100);
                return (
                  <div key={p.source} className="flex items-center gap-3">
                    <div className="w-32 text-xs font-semibold text-slate-600 shrink-0 truncate">{p.source}</div>
                    <div className="flex-1 h-5 rounded-full bg-slate-100 overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${pct}%`, background: PROV_COLORS[i % PROV_COLORS.length], minWidth: p.count ? 6 : 0 }} />
                    </div>
                    <div className="w-20 text-right text-xs font-bold text-slate-900">{p.count} <span className="text-slate-400 font-normal">({pct}%)</span></div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="top-pages-block">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2 font-heading font-extrabold text-slate-900">
            <FileText size={18} className="text-[#E05638]" /> Pages les plus visitées
          </div>
          {(an?.top_pages || []).length === 0 ? (
            <div className="p-8 text-center text-slate-400 text-sm">Pas encore de visites.</div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {(an?.top_pages || []).map((p, i) => (
                <li key={p.path || i} className="px-4 py-2.5 flex items-center justify-between gap-3">
                  <span className="text-sm text-slate-700 truncate">{p.path}</span>
                  <span className="text-sm font-extrabold text-[#E05638] shrink-0">{p.count}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Partages */}
      <h2 className="mt-10 font-heading text-xl font-extrabold text-slate-900 flex items-center gap-2"><Share2 size={20} className="text-[#E05638]" /> Partages sur les réseaux</h2>
      <div className="mt-4 grid grid-cols-3 gap-4">
        <Kpi icon={Tag} label="Offres" value={data?.offers?.total || 0} />
        <Kpi icon={Store} label="Fiches partenaires" value={data?.businesses?.total || 0} />
        <Kpi icon={Newspaper} label="Articles de blog" value={data?.blog?.total || 0} />
      </div>

      <div className="mt-6 bg-white border border-slate-200 rounded-2xl p-5">
        <div className="font-heading font-extrabold text-slate-900 mb-4">Répartition par réseau (tous partages)</div>
        <TotalsBar channels={channels} totals={data?.channel_totals || {}} grand={data?.total || 0} />
      </div>

      <div className="mt-6 grid lg:grid-cols-2 gap-6">
        <TopTable title="Offres les plus partagées" icon={Tag} rows={data?.offers?.top || []} channels={channels}
          nameKey="title" linkFor={(r) => `${site}/api/offre-vitrine/${r.id}`} />
        <TopTable title="Fiches les plus partagées" icon={Store} rows={data?.businesses?.top || []} channels={channels}
          nameKey="name" linkFor={(r) => `${site}/api/vitrine/${r.id}`} />
        <TopTable title="Articles les plus partagés" icon={Newspaper} rows={data?.blog?.top || []} channels={channels}
          nameKey="title" linkFor={(r) => `${site}/api/blog-vitrine/${r.slug}`} />
      </div>
      </>
      )}
    </div>
  );
}
